import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSnackbar } from "JS/Helpers/Snackbar";
import { messaging } from "JS/Helpers/UserMessaging";
import { captureSentryError } from "JS/Helpers/SentryHelper";
import {
    checklistBanners,
    describeChecklistResponse,
    isValidChecklistResponse,
    selectChecklistBanner,
} from "JS/Helpers/ChecklistConstants";
import { ChecklistItem } from "JS/Models/Checklist";
import { ChecklistService } from "JS/Services/Checklist/ChecklistService";

const service = new ChecklistService();

export const useChecklist = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [items, setItems] = useState<ChecklistItem[]>([]);
    const [pendingItems, setPendingItems] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);

    const itemsRef = useRef<ChecklistItem[]>([]);
    const inFlightRef = useRef<Set<string>>(new Set());
    const failureNotifiedRef = useRef(false);
    const refreshFailureReportedRef = useRef(false);
    const mountedRef = useRef(true);
    const loadInFlightRef = useRef(false);

    const applyItems = useCallback((next: ChecklistItem[]) => {
        itemsRef.current = next;
        setItems(next);
    }, []);

    const setChecked = useCallback(
        (itemNumber: string, isChecked: boolean) => {
            applyItems(
                itemsRef.current.map((item) =>
                    item.itemNumber === itemNumber
                        ? { ...item, isChecked }
                        : item,
                ),
            );
        },
        [applyItems],
    );

    // Checks are one-way, so a local tick is a floor: no payload may un-tick an indicator.
    const applyLoadedItems = useCallback(
        (next: ChecklistItem[]) => {
            const checkedLocally = new Set(
                itemsRef.current
                    .filter((item) => item.isChecked)
                    .map((item) => item.itemNumber),
            );

            applyItems(
                next.map((item) =>
                    checkedLocally.has(item.itemNumber)
                        ? { ...item, isChecked: true }
                        : item,
                ),
            );
        },
        [applyItems],
    );

    const reportLoadFailure = useCallback((error: Error, silent: boolean) => {
        if (silent) {
            if (refreshFailureReportedRef.current) return;
            refreshFailureReportedRef.current = true;
        }

        captureSentryError(error, {
            location: silent ? `ChecklistItemsRefresh` : `ChecklistItems`,
        });
    }, []);

    const loadItems = useCallback(
        async (silent: boolean) => {
            if (silent && loadInFlightRef.current) return;
            loadInFlightRef.current = true;

            try {
                const response = await service.getChecklistItems();
                if (!mountedRef.current) return;

                if (isValidChecklistResponse(response?.data)) {
                    // An empty payload behind a populated list is a hiccup, not a wipe.
                    if (
                        silent &&
                        response.data.length === 0 &&
                        itemsRef.current.length > 0
                    )
                        return;

                    applyLoadedItems(response.data);
                    setLoadFailed(false);
                    failureNotifiedRef.current = false;
                    return;
                }

                if (!silent) setLoadFailed(true);
                reportLoadFailure(
                    new Error(
                        `Unusable checklist response: ${describeChecklistResponse(
                            response?.data,
                        )}`,
                    ),
                    silent,
                );
            } catch (err) {
                if (!mountedRef.current) return;
                if (!silent) setLoadFailed(true);
                reportLoadFailure(err, silent);
            } finally {
                loadInFlightRef.current = false;
            }
        },
        [applyLoadedItems, reportLoadFailure],
    );

    useEffect(() => {
        mountedRef.current = true;

        loadItems(false).finally(() => {
            if (mountedRef.current) setLoading(false);
        });

        return () => {
            mountedRef.current = false;
        };
    }, [loadItems]);

    useEffect(() => {
        const onVisibilityChange = () => {
            if (globalThis.document.visibilityState !== "visible") return;
            if (!globalThis.navigator.onLine) return;

            loadItems(true);
        };

        globalThis.document.addEventListener(
            "visibilitychange",
            onVisibilityChange,
        );

        return () =>
            globalThis.document.removeEventListener(
                "visibilitychange",
                onVisibilityChange,
            );
    }, [loadItems]);

    useEffect(() => {
        if (!loadFailed || failureNotifiedRef.current) return;

        failureNotifiedRef.current = true;
        enqueueSnackbar(messaging.checklist.loadFailed, { variant: "error" });
    }, [loadFailed, enqueueSnackbar]);

    const checkItem = useCallback(
        (itemNumber: string) => {
            const item = itemsRef.current.find(
                (candidate) => candidate.itemNumber === itemNumber,
            );
            if (!item || item.isChecked || inFlightRef.current.has(itemNumber))
                return;

            if (!globalThis.navigator.onLine) {
                enqueueSnackbar(messaging.checklist.offline, {
                    variant: "warning",
                });
                return;
            }

            inFlightRef.current.add(itemNumber);
            setPendingItems((prev) => [...prev, itemNumber]);
            setChecked(itemNumber, true);

            service
                .checkChecklistItem(itemNumber)
                .catch((err) => {
                    if (err?.response?.status === 409) return;

                    setChecked(itemNumber, false);
                    enqueueSnackbar(messaging.checklist.checkFailed, {
                        variant: "error",
                    });
                    captureSentryError(err, {
                        location: `ChecklistCheckItem`,
                    });
                })
                .finally(() => {
                    inFlightRef.current.delete(itemNumber);
                    setPendingItems((prev) =>
                        prev.filter((pending) => pending !== itemNumber),
                    );
                });
        },
        [setChecked, enqueueSnackbar],
    );

    const checkedCount = useMemo(
        () => items.filter((item) => item.isChecked).length,
        [items],
    );

    const bannerUrl = useMemo(
        () => selectChecklistBanner(checklistBanners, checkedCount),
        [checkedCount],
    );

    return {
        items,
        pendingItems,
        bannerUrl,
        loading,
        loadFailed,
        checkItem,
    };
};
